import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

const PREFETCH_CACHE = new Map();
const PREFETCH_CACHE_LIMIT = 60;
const PREFETCH_TRIGGER_EVENTS = ['pointerenter', 'focus'];
const PRODUCT_DATA_ELEMENT_ID = 'product-data';
const BULK_PREFETCH_IDLE_TIMEOUT = 1500;

const requestIdle = typeof window !== 'undefined' && window.requestIdleCallback
  ? (callback, options) => window.requestIdleCallback(callback, options)
  : (callback, options) => window.setTimeout(() => callback({ didTimeout: true, timeRemaining: () => 0 }), options?.timeout || 200);

const cancelIdle = typeof window !== 'undefined' && window.cancelIdleCallback
  ? (handle) => window.cancelIdleCallback(handle)
  : (handle) => window.clearTimeout(handle);

const PRODUCT_DATA_CACHE = new Map();

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {boolean} */
  #autoSelecting = false;

  /** @type {number} */
  #fallbackAttempts = 0;

  /** @type {Set<Element>} */
  #prefetchTargets = new Set();

  /** @type {string[]} */
  #bulkPrefetchQueue = [];

  /** @type {number | undefined} */
  #bulkPrefetchHandle;

  /** @type {boolean} */
  #bulkPrefetchScheduled = false;

  /** @type {Map<number, Map<string, string>> | undefined} */
  #optionValueIdMap;

  /** @type {any} */
  #productData;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.#registerPrefetchTargets(fieldsets);
    this.#scheduleBulkPrefetch();
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupPrefetchTargets();
    if (this.#bulkPrefetchHandle) {
      cancelIdle(this.#bulkPrefetchHandle);
      this.#bulkPrefetchHandle = undefined;
    }
    this.#bulkPrefetchQueue = [];
    this.#bulkPrefetchScheduled = false;
  }

  #registerPrefetchTargets(fieldsets) {
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input[type="radio"][data-option-value-id]') ?? []);
      radios.forEach((radio) => {
        if (this.#prefetchTargets.has(radio)) return;
        PREFETCH_TRIGGER_EVENTS.forEach((eventName) => {
          radio.addEventListener(eventName, this.#handlePrefetchTrigger, { passive: true });
        });
        this.#prefetchTargets.add(radio);
      });
    });
  }

  #cleanupPrefetchTargets() {
    this.#prefetchTargets.forEach((target) => {
      PREFETCH_TRIGGER_EVENTS.forEach((eventName) => {
        target.removeEventListener(eventName, this.#handlePrefetchTrigger);
      });
    });
    this.#prefetchTargets.clear();
    this.#optionValueIdMap = undefined;
  }

  #refreshPrefetchTargets() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));
    this.#cleanupPrefetchTargets();
    this.#registerPrefetchTargets(fieldsets);
    this.#scheduleBulkPrefetch(true);
  }

  #scheduleBulkPrefetch(force = false) {
    if (navigator.connection?.saveData) return;
    if (this.dataset.templateProductMatch !== 'true') return;
    if (this.#bulkPrefetchScheduled && !force) return;

    const run = () => {
      this.#bulkPrefetchHandle = undefined;
      this.#bulkPrefetchScheduled = false;
      this.#startBulkPrefetch();
    };

    if (force) {
      run();
      return;
    }

    this.#bulkPrefetchScheduled = true;
    this.#bulkPrefetchHandle = requestIdle(run, { timeout: BULK_PREFETCH_IDLE_TIMEOUT });
  }

  #startBulkPrefetch() {
    if (navigator.connection?.saveData) return;
    const combinations = this.#computeVariantOptionValueSets();
    if (!combinations.length) return;

    const referenceOption =
      this.selectedOption || this.querySelector('input[type="radio"][data-option-value-id]') || undefined;
    if (!referenceOption) return;

    const seen = new Set();
    const urls = combinations
      .map((optionIds) =>
        this.buildRequestUrl(referenceOption, null, [], {
          optionValuesOverride: optionIds,
          dryRun: true,
        })
      )
      .filter((url) => typeof url === 'string' && url.length > 0);

    this.#bulkPrefetchQueue = urls.filter((url) => {
      if (seen.has(url)) return false;
      if (PREFETCH_CACHE.has(url)) return false;
      seen.add(url);
      return true;
    });

    if (!this.#bulkPrefetchQueue.length) return;
    this.#processBulkPrefetchQueue();
  }

  #processBulkPrefetchQueue() {
    if (!this.#bulkPrefetchQueue.length) {
      this.#bulkPrefetchHandle = undefined;
      return;
    }

    const requestUrl = this.#bulkPrefetchQueue.shift();
    if (!requestUrl) {
      this.#processBulkPrefetchQueue();
      return;
    }

    const promise = this.#ensurePrefetchEntry(requestUrl, { lowPriority: true });
    if (!promise) {
      this.#scheduleNextBulkPrefetchStep();
      return;
    }

    promise.finally(() => this.#scheduleNextBulkPrefetchStep());
  }

  #scheduleNextBulkPrefetchStep() {
    if (!this.#bulkPrefetchQueue.length) {
      this.#bulkPrefetchHandle = undefined;
      return;
    }

    this.#bulkPrefetchHandle = requestIdle(() => this.#processBulkPrefetchQueue(), {
      timeout: BULK_PREFETCH_IDLE_TIMEOUT,
    });
  }

  #computeVariantOptionValueSets() {
    const productData = this.#getProductData();
    if (!productData?.variants?.length) return [];

    const optionMap = this.#getOptionValueIdMap();
    if (!optionMap.size) return [];

    const combinations = [];
    productData.variants.forEach((variant) => {
      if (!variant?.options?.length || variant.hidden === true) return;
      const optionIds = [];

      variant.options.forEach((value, index) => {
        const fieldMap = optionMap.get(index);
        const id = fieldMap?.get(this.#normalizeOptionValue(value));
        if (!id) return;
        optionIds.push(id);
      });

      if (optionIds.length === variant.options.length) {
        combinations.push(optionIds);
      }
    });

    return combinations;
  }

  #getOptionValueIdMap() {
    if (this.#optionValueIdMap) return this.#optionValueIdMap;
    const optionMap = new Map();

    const radios = this.querySelectorAll('input[type="radio"][data-fieldset-index][data-option-value-id]');
    radios.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const fieldsetIndex = Number.parseInt(input.dataset.fieldsetIndex || '', 10);
      if (Number.isNaN(fieldsetIndex)) return;
      const optionId = input.dataset.optionValueId;
      if (!optionId) return;
      const normalizedValue = this.#normalizeOptionValue(input.value);
      if (!normalizedValue) return;
      if (!optionMap.has(fieldsetIndex)) {
        optionMap.set(fieldsetIndex, new Map());
      }
      optionMap.get(fieldsetIndex)?.set(normalizedValue, optionId);
    });

    this.#optionValueIdMap = optionMap;
    return optionMap;
  }

  #normalizeOptionValue(value) {
    if (typeof value === 'string') return value.trim().toLowerCase();
    if (value == null) return '';
    return String(value).trim().toLowerCase();
  }

  #getProductData() {
    if (this.#productData?.id && String(this.#productData.id) === String(this.dataset.productId || '')) {
      return this.#productData;
    }

    const cacheKey = this.dataset.productId ? String(this.dataset.productId) : 'default';
    if (PRODUCT_DATA_CACHE.has(cacheKey)) {
      this.#productData = PRODUCT_DATA_CACHE.get(cacheKey);
      return this.#productData;
    }

    if (typeof document === 'undefined') return undefined;
    const script = document.getElementById(PRODUCT_DATA_ELEMENT_ID);
    const text = script?.textContent?.trim();
    if (!text) return undefined;

    try {
      const data = JSON.parse(text);
      if (data?.id) {
        PRODUCT_DATA_CACHE.set(String(data.id), data);
      }
      PRODUCT_DATA_CACHE.set(cacheKey, data);
      this.#productData = data;
      return data;
    } catch (error) {
      console.warn('Unable to parse product data payload', error);
      return undefined;
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    if (!this.#autoSelecting) {
      this.#fallbackAttempts = 0;
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {{ optionValuesOverride?: string[]; dryRun?: boolean }} [options] - Additional configuration.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], options = {}) {
    const { optionValuesOverride, dryRun = false } = options;
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    const productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    if (!productUrl) return '';

    if (!dryRun) {
      this.#pendingRequestUrl = productUrl;
    }

    // Merge existing product URL params with current URL params so we preserve tracking / context.
    const url = new URL(productUrl, typeof window !== 'undefined' ? window.location.origin : undefined);
    const mergedParams = new URLSearchParams(url.search);
    if (typeof window !== 'undefined') {
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, key) => {
        if (!mergedParams.has(key)) {
          mergedParams.append(key, value);
        }
      });
    }

    // Carry selected selling plan so returned HTML is plan-aware
    const sellingPlanInput = this.closest('.shopify-section, dialog')?.querySelector(
      'selling-plan-picker input[name="selling_plan"]:checked'
    );
    const sellingPlanId = sellingPlanInput?.value;

    if (optionValuesOverride?.length) {
      mergedParams.set('option_values', optionValuesOverride.join(','));
    } else if (this.selectedOptionsValues.length && !source) {
      mergedParams.set('option_values', this.selectedOptionsValues.join(','));
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        mergedParams.set('option_values', sourceSelectedOptionsValues.join(','));
      } else {
        mergedParams.set('option_values', selectedOption.dataset.optionValueId || '');
      }
    }

    if (sellingPlanId) {
      mergedParams.set('selling_plan', sellingPlanId);
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      mergedParams.set('section_id', 'section-rendering-product-card');
    }

    url.search = mergedParams.toString();

    // Return relative path for same-origin URLs to match previous behavior.
    if (typeof window !== 'undefined' && url.origin === window.location.origin) {
      return `${url.pathname}${url.search}`;
    }

    return url.toString();
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    const cacheEntry = PREFETCH_CACHE.get(requestUrl);

    if (cacheEntry?.responseText) {
      this.#pendingRequestUrl = undefined;
      this.#processVariantResponse(cacheEntry.responseText, shouldMorphMain);
      return;
    }

    if (cacheEntry?.promise) {
      cacheEntry.promise
        .then((responseText) => {
          if (!responseText) return;
          this.#pendingRequestUrl = undefined;
          this.#processVariantResponse(responseText, shouldMorphMain);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          PREFETCH_CACHE.delete(requestUrl);
          this.#performNetworkRequest(requestUrl, shouldMorphMain);
        });
      return;
    }

    this.#performNetworkRequest(requestUrl, shouldMorphMain);
  }

  #performNetworkRequest(requestUrl, shouldMorphMain) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        this.#storePrefetchResult(requestUrl, responseText);
        this.#processVariantResponse(responseText, shouldMorphMain);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  #processVariantResponse(responseText, shouldMorphMain) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
    if (!textContent) return;

    const variantData = textContent.trim() ? JSON.parse(textContent) : null;

    if (shouldMorphMain) {
      this.updateMain(html);
      return;
    }

    const newProduct = this.updateVariantPicker(html);

    if (!this.#shouldDispatchVariantUpdate(variantData)) {
      return;
    }

    const sourceId = this.selectedOptionId;
    if (sourceId) {
      this.dispatchEvent(
        new VariantUpdateEvent(variantData, sourceId, {
          html,
          productId: this.dataset.productId ?? '',
          newProduct,
        })
      );
    }
  }

  #handlePrefetchTrigger = (event) => {
    const option = event.currentTarget;
    if (!(option instanceof HTMLElement)) return;
    this.#prefetchOption(option);
  };

  #prefetchOption(option) {
    if (navigator.connection?.saveData) return;

    const optionValues = this.#getOptionValuesFor(option);
    if (!optionValues?.length) return;

    const requestUrl = this.buildRequestUrl(option, null, [], {
      optionValuesOverride: optionValues,
      dryRun: true,
    });

    if (!requestUrl) return;

    this.#ensurePrefetchEntry(requestUrl);
  }

  #getOptionValuesFor(option) {
    const optionValueId = option.dataset.optionValueId;
    if (!optionValueId) return undefined;

    const values = [...this.selectedOptionsValues];

    if (option instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(option.dataset.fieldsetIndex || '', 10);
      if (!Number.isNaN(fieldsetIndex)) {
        values[fieldsetIndex] = optionValueId;
        return values;
      }
    }

    if (!values.includes(optionValueId)) {
      values.push(optionValueId);
    }

    return values;
  }

  #ensurePrefetchEntry(requestUrl, options = {}) {
    const existing = PREFETCH_CACHE.get(requestUrl);
    if (existing?.responseText || existing?.promise) return existing?.promise;

    const fetchOptions = options.lowPriority ? { priority: 'low' } : undefined;
    const fetchPromise = fetch(requestUrl, fetchOptions)
      .then((response) => response.text())
      .then((responseText) => {
        this.#storePrefetchResult(requestUrl, responseText);
        return responseText;
      });

    PREFETCH_CACHE.set(requestUrl, { promise: fetchPromise, timestamp: Date.now() });

    fetchPromise.catch((error) => {
      if (error?.name !== 'AbortError') {
        console.error(error);
      }
      const entry = PREFETCH_CACHE.get(requestUrl);
      if (entry?.promise === fetchPromise) {
        PREFETCH_CACHE.delete(requestUrl);
      }
    });

    return fetchPromise;
  }

  #storePrefetchResult(requestUrl, responseText) {
    PREFETCH_CACHE.set(requestUrl, { responseText, timestamp: Date.now() });
    this.#prunePrefetchCache();
  }

  #prunePrefetchCache() {
    if (PREFETCH_CACHE.size <= PREFETCH_CACHE_LIMIT) return;
    const entries = [...PREFETCH_CACHE.entries()].sort((a, b) => a[1].timestamp - b[1].timestamp);
    while (entries.length && PREFETCH_CACHE.size > PREFETCH_CACHE_LIMIT) {
      const [key] = entries.shift() || [];
      if (key) {
        PREFETCH_CACHE.delete(key);
      }
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.#refreshPrefetchTargets();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * @param {any} variantData
   */
  #shouldDispatchVariantUpdate(variantData) {
    const variantAvailable = Boolean(variantData?.id) && variantData?.available !== false;

    if (variantAvailable) {
      this.#fallbackAttempts = 0;
      return true;
    }

    if (this.#attemptAutoSelection()) {
      return false;
    }

    this.#fallbackAttempts = 0;
    return true;
  }

  #attemptAutoSelection() {
    if (this.#fallbackAttempts >= 2) return false;

    const fallbackInput = this.#findFallbackInput();
    if (!fallbackInput) return false;

    this.#fallbackAttempts += 1;
    this.#autoSelecting = true;
    fallbackInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.#autoSelecting = false;

    return true;
  }

  #findFallbackInput() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));

    for (let index = fieldsets.length - 1; index >= 0; index--) {
      const radios = Array.from(fieldsets[index].querySelectorAll('input[type="radio"]'));
      if (!radios.length) continue;

      const selected = radios.find((radio) => radio.checked) ?? null;

      if (selected && this.#isRadioAvailable(selected)) {
        continue;
      }

      const fallback = radios.find((radio) => this.#isRadioAvailable(radio));

      if (fallback) {
        return fallback;
      }
    }

    return null;
  }

  /**
   * @param {HTMLInputElement} input
   */
  #isRadioAvailable(input) {
    if (!input) return false;
    if (input.disabled) return false;

    const availability = input.dataset.optionAvailable;
    if (availability === 'false') return false;

    return true;
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
